var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { TypeRegistryMap, RootScopedDag, extractCreatePayloadType, formatValidationFailure, ValidationRejectedError, } from "@hyper-hyper-space/hhs3_mvt";
import { fetchInit } from "@hyper-hyper-space/hhs3_sync";
export class Replica {
    constructor(opts) {
        var _a;
        this.backends = new Map();
        this.meshes = new Map();
        this.registry = new TypeRegistryMap();
        this.objects = new Map();
        this.rootIds = new Set();
        this.backendByDagId = new Map();
        this.dagCache = new Map();
        this.crypto = opts.crypto;
        this.hashSuite = opts.hashSuite;
        this.config = (_a = opts.config) !== null && _a !== void 0 ? _a : {};
    }
    // --- Attachment and registration ---
    attachBackend(label, backend) {
        this.backends.set(label, backend);
    }
    attachMesh(label, mesh) {
        this.meshes.set(label, mesh);
    }
    registerType(name, factory) {
        this.registry.register(name, factory);
    }
    // --- Lifecycle ---
    destroy() {
        return __awaiter(this, void 0, void 0, function* () {
            for (const id of [...this.rootIds]) {
                const obj = this.objects.get(id);
                if (obj === undefined)
                    continue;
                try {
                    yield obj.destroy();
                }
                catch (_) { /* best effort */ }
                this.releaseObject(id);
            }
            this.rootIds.clear();
            this.objects.clear();
            this.backendByDagId.clear();
            this.dagCache.clear();
        });
    }
    /** @deprecated Use destroy() */
    close() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.destroy();
        });
    }
    getRootIds() {
        return this.rootIds;
    }
    // --- Object registry ---
    registerObject(obj) {
        this.recordObject(obj);
    }
    unregisterObject(id) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.rootIds.has(id)) {
                throw new Error(`Cannot unregister root object '${id}'`);
            }
            const obj = this.objects.get(id);
            if (obj === undefined)
                return;
            yield obj.destroy();
            this.releaseObject(id);
        });
    }
    recordObject(obj) {
        const id = obj.getId();
        this.objects.set(id, obj);
        const label = obj.getBackendLabel();
        this.backendByDagId.set(id, label);
    }
    releaseObject(id) {
        this.objects.delete(id);
        this.backendByDagId.delete(id);
        for (const key of [...this.dagCache.keys()]) {
            if (key === id || key.endsWith(`:${id}`)) {
                this.dagCache.delete(key);
            }
        }
    }
    // --- Object creation (idempotent) ---
    createObject(createPayload_1) {
        return __awaiter(this, arguments, void 0, function* (createPayload, backendLabel = 'default') {
            const typeId = extractCreatePayloadType(createPayload);
            if (typeId === undefined) {
                throw new Error('create payload missing type');
            }
            const factory = yield this.registry.lookup(typeId);
            const id = yield factory.computeRootObjectId(createPayload, this, undefined);
            const cached = this.objects.get(id);
            if (cached !== undefined) {
                return cached;
            }
            const backend = this.backends.get(backendLabel);
            if (backend === undefined) {
                throw new Error(`No backend attached with label '${backendLabel}'`);
            }
            const result = yield factory.validateCreationPayload(createPayload, this, undefined);
            if (!result.valid) {
                throw new ValidationRejectedError(formatValidationFailure(result.why), result.why);
            }
            const { dag, created } = yield backend.getOrCreateDag(id, { type: typeId });
            this.backendByDagId.set(id, backendLabel);
            this.dagCache.set(`${backendLabel}:${id}`, dag);
            if (created) {
                const scopedDag = new RootScopedDag(dag);
                yield factory.executeCreationPayload(createPayload, this, scopedDag);
            }
            const obj = yield factory.loadObject(id, this, { backendLabel });
            this.recordObject(obj);
            this.rootIds.add(id);
            return obj;
        });
    }
    // --- Remote object fetching ---
    fetchObject(id, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const meshLabel = (_a = opts === null || opts === void 0 ? void 0 : opts.meshLabel) !== null && _a !== void 0 ? _a : 'default';
            const backendLabel = (_b = opts === null || opts === void 0 ? void 0 : opts.backendLabel) !== null && _b !== void 0 ? _b : 'default';
            const existing = this.objects.get(id);
            if (existing !== undefined)
                return existing;
            const mesh = this.getMesh(meshLabel);
            const swarm = mesh.createSwarm(id);
            try {
                swarm.activate();
                const createPayload = yield fetchInit(id, [swarm], this.hashSuite, opts === null || opts === void 0 ? void 0 : opts.timeoutMs);
                return yield this.createObject(createPayload, backendLabel);
            }
            finally {
                swarm.destroy();
            }
        });
    }
    // --- RContext implementation ---
    getCrypto() {
        return this.crypto;
    }
    getHashSuite() {
        return this.hashSuite;
    }
    getConfig() {
        return this.config;
    }
    getRegistry() {
        return this.registry;
    }
    getObject(id) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.objects.get(id);
        });
    }
    getBackendLabel(id) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.backendByDagId.get(id);
        });
    }
    getDag(id, backendLabel) {
        return __awaiter(this, void 0, void 0, function* () {
            const cacheKey = backendLabel ? `${backendLabel}:${id}` : id;
            const cached = this.dagCache.get(cacheKey);
            if (cached !== undefined)
                return cached;
            const label = backendLabel !== null && backendLabel !== void 0 ? backendLabel : this.tryResolveBackendLabelForId(id);
            if (label === undefined)
                return undefined;
            const backend = this.backends.get(label);
            if (backend === undefined)
                return undefined;
            const d = yield backend.openDag(id);
            if (d === undefined)
                return undefined;
            this.dagCache.set(`${label}:${id}`, d);
            this.backendByDagId.set(id, label);
            return d;
        });
    }
    getMesh(label) {
        const mesh = this.meshes.get(label);
        if (mesh === undefined) {
            throw new Error(`No mesh attached with label '${label}'`);
        }
        return mesh;
    }
    // --- Internal helpers ---
    tryResolveBackendLabelForId(id) {
        const label = this.backendByDagId.get(id);
        if (label !== undefined)
            return label;
        if (this.backends.size === 1) {
            return this.backends.keys().next().value;
        }
        if (this.backends.has('default'))
            return 'default';
        return undefined;
    }
}
